// Production canonical domain. Update if the live domain differs.
export const SITE_URL = "https://www.switchnow.in";
export const SITE_NAME = "SWITCH";
export const OG_IMAGE = `${SITE_URL}/assets/og-image.jpg`;
export const DEFAULT_TITLE = "SWITCH: Strategy Built to Be Executed.";
export const DEFAULT_DESC =
  "SWITCH is a problem-led growth company. We find what is holding growth back, build strategy around execution, and stay in the room until it moves.";
export const DEFAULT_KEYWORDS =
  "growth strategy consulting, strategy execution, business transformation, go-to-market strategy, growth consulting India, CXO growth advisory, problem-led consulting, scale-up consulting, revenue growth strategy";

// Per-route keywords for the four growth situations (/what-we-solve/<slug>).
export const SITUATION_KEYWORDS = {
  "growth-has-stalled":
    "growth has stalled, stalled revenue growth, plateaued growth, declining market share, marketing not converting, sales underperformance, growth turnaround",
  "ready-to-scale":
    "ready to scale, scale-up strategy, scaling a business, founder dependency, regional to national expansion, operating discipline, growth systems",
  "reposition-or-transform":
    "brand repositioning, business transformation, business model change, proposition strategy, market relevance, repositioning strategy",
  "create-enterprise-value":
    "enterprise value creation, profitable growth, valuation improvement, investor growth strategy, EBITDA improvement, board growth plan, value creation plan",
};

// Per-route title + description + keywords. Keys are pathnames (no trailing slash).
const _og = (f) => `${SITE_URL}/assets/${f}`;
export const SITUATION_IMAGES = {
  "growth-has-stalled": _og("og-situation-growth-has-stalled.jpg"),
  "ready-to-scale": _og("og-situation-ready-to-scale.jpg"),
  "reposition-or-transform": _og("og-situation-reposition-or-transform.jpg"),
  "create-enterprise-value": _og("og-situation-create-enterprise-value.jpg"),
};

export const PAGE_SEO = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
    keywords: DEFAULT_KEYWORDS,
  },
  "/what-we-solve": {
    title: "What We Solve: Four Ways Growth Gets Stuck | SWITCH",
    description:
      "Enter by the growth problem, not the job title. Explore the four situations where growth stalls and how SWITCH gets it moving again.",
    keywords:
      "why growth stalls, revenue growth consulting, business turnaround, market entry strategy, scaling strategy, business repositioning, enterprise value creation, growth diagnosis",
    image: _og("og-what-we-solve.jpg"),
  },
  "/how-we-work": {
    title: "How We Work: The SWITCH Model | SWITCH",
    description:
      "Four moves that turn a growth problem into growth: diagnose before prescribing, build strategy around execution, bring the right expertise, and stay through implementation.",
    keywords:
      "growth consulting model, diagnostic-led consulting, strategy execution framework, operating model design, execution-first consulting, hands-on growth consulting",
    image: _og("og-how-we-work.jpg"),
  },
  "/our-work": {
    title: "Our Work: Proof, Not Promises | SWITCH",
    description:
      "We measure our work by the growth it creates. Verified case studies of businesses set back in motion.",
    keywords:
      "growth consulting case studies, business transformation results, go-to-market case studies, scale-up success stories, execution proof",
    image: _og("og-our-work.jpg"),
  },
  "/about": {
    title: "About: A Growth Company Built for Execution | SWITCH",
    description:
      "SWITCH connects the disciplines that affect growth, finds the real constraint, and stays until the business moves. Growth is a system, not a slogan.",
    keywords:
      "problem-led growth company, growth consultancy India, senior operators and strategists, execution-first consulting, growth as a system",
    image: _og("og-about.jpg"),
  },
  "/team": {
    title: "Our Team: The People Who Stay in the Room | SWITCH",
    description:
      "Senior operators and strategists who diagnose the real constraint, build strategy around execution, and stay until the business moves.",
    keywords:
      "growth consultancy team, senior operators and strategists, SWITCH leadership, growth consulting partners, execution-first team",
    image: _og("og-about.jpg"),
  },
  "/insights": {
    title: "Switchboard: Thinking Worth Your Time | SWITCH",
    description:
      "The Switchboard. Practical perspectives on growth, scale and transformation from the SWITCH team.",
    keywords:
      "growth insights, CXO perspectives, strategy execution insights, India growth commentary, FMCG pharma retail manufacturing growth, business scale and transformation",
    image: _og("og-insights.jpg"),
  },
  "/contact": {
    title: "Contact: Let's Talk | SWITCH",
    description:
      "Bring us your growth problem, or send a message. Start the conversation with SWITCH.",
    keywords:
      "contact growth consultants, growth consulting enquiry, talk to SWITCH, bring your growth problem",
    image: _og("og-contact.jpg"),
  },
  "/careers": {
    title: "Careers: Work With Us | SWITCH",
    description:
      "Work on problems that do not fit neatly into one discipline. Join a growth company built to turn strategy into execution.",
    keywords:
      "growth consulting careers, strategy consulting jobs India, management consulting careers, cross-disciplinary consulting roles",
    image: _og("og-careers.jpg"),
  },
  "/legal": {
    title: "Legal: Privacy, Terms & Cookies | SWITCH",
    description:
      "How SWITCH handles your information, the terms for using this site, and our cookie policy, all in one place.",
    keywords: "SWITCH privacy policy, terms of use, cookie policy",
    image: _og("og-legal.jpg"),
  },
  "/find-your-switch": {
    title: "Find Your Switch | SWITCH",
    description:
      "Describe what is not moving and get an instant read on where your growth is stuck.",
    keywords:
      "growth diagnostic, find your growth problem, growth problem diagnosis, AI growth assistant, where growth is stuck",
    image: _og("og-find-your-switch.jpg"),
  },
};

export const organizationLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/assets/wordmark.png`,
  description: DEFAULT_DESC,
  email: "hello@switchnow.in",
  sameAs: [],
};

export const websiteLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: SITE_URL,
};

export const serviceLd = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "SWITCH Growth Consulting",
  url: `${SITE_URL}/how-we-work`,
  description:
    "Problem-led growth consulting that brings strategy and execution together across every function.",
  areaServed: "Global",
  provider: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
};

export const breadcrumbLd = (crumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    item: `${SITE_URL}${c.path}`,
  })),
});

export const articleLd = (blog, url) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: blog.seoTitle || blog.title,
  description: blog.excerpt,
  image: (blog.seoImage || blog.image || "").startsWith("http") ? (blog.seoImage || blog.image) : `${SITE_URL}${blog.seoImage || blog.image}`,
  datePublished: blog.date,
  dateModified: blog.date,
  articleSection: blog.situation,
  keywords: (blog.tags || []).join(", "),
  author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/wordmark.png` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": url },
});

export const caseLd = (c, url) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: c.seoTitle || c.headline,
  description: (c.opportunity || [""])[0],
  image: (c.seoImage || c.image || "").startsWith("http") ? (c.seoImage || c.image) : `${SITE_URL}${c.seoImage || c.image}`,
  articleSection: c.situation,
  about: c.service,
  author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    logo: { "@type": "ImageObject", url: `${SITE_URL}/assets/wordmark.png` },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": url },
});

export const faqLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((it) => ({
    "@type": "Question",
    name: it.q,
    acceptedAnswer: { "@type": "Answer", text: it.a },
  })),
});
